import { useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import { Dimensions } from "react-native";
import { View, useMedia } from "tamagui";

import {
  useIsChatMutationPending,
  useSendNoriMessage,
} from "@/api/mutations/conversation";
import { Suggestion, Typer, YStack } from "@/components";
import { shortBrandName } from "@/config/brand";
import { UseCase, useUseCaseSuggestions } from "@/hooks/suggestions";
import { useConversationComposition } from "@/store/conversation";
import { Agent } from "@/types/agent";

interface SuggestionWithPrompt {
  title?: string;
  prompt: string;
  agent: Agent;
}

const { height: screenHeight } = Dimensions.get("window");

export default function UseCasePrompts({ v3 = false }: { v3?: boolean }) {
  const { lg } = useMedia();
  const { useCase } = useLocalSearchParams();
  const { sendNoriMessage } = useSendNoriMessage();
  const isPending = useIsChatMutationPending();
  const { setInputControl, clearInputControl } = useConversationComposition();

  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [visibleSuggestions, setVisibleSuggestions] = useState(0);

  const useCaseString = Array.isArray(useCase)
    ? (useCase?.[0] ?? "")
    : (useCase ?? "");

  const { prompts, headerV3, chips } = useUseCaseSuggestions(
    useCaseString as UseCase,
  );

  let suggs = prompts ?? [];

  const mergedSuggestions: SuggestionWithPrompt[] =
    suggs as SuggestionWithPrompt[];

  // Show suggestions one at a time after typing is complete
  useEffect(() => {
    if (isTypingComplete && visibleSuggestions < mergedSuggestions.length) {
      const timer = setTimeout(() => {
        setVisibleSuggestions((prev) => prev + 1);
      }, 200); // 200ms delay between each suggestion
      return () => clearTimeout(timer);
    }
  }, [isTypingComplete, visibleSuggestions, mergedSuggestions.length]);

  // Set up input control for ss when available, clear when use case changes or chips unavailable
  useEffect(() => {
    if (chips && chips.length > 0) {
      setInputControl({
        type: "multiple_choice",
        suggestions: chips,
        selected: [],
      });
    } else {
      clearInputControl();
    }
    return () => {
      clearInputControl();
    };
  }, [chips, setInputControl, clearInputControl]);

  let typerProps;
  if (v3) {
    typerProps = headerV3!.assistantMessage;
  } else {
    typerProps = {
      content: [
        `Hey there 👋 I’m ${shortBrandName}, your free health assistant here to take the hard stuff off your plate, simplify healthcare, and help you reach your goals.`,
        "\n\nPick a topic below to start or just share what’s on your mind.",
        "\n\nHow can I help?",
      ],
    };
  }

  let minHeight: number | undefined = lg
    ? screenHeight / 3
    : screenHeight / 2.15;
  if (v3) minHeight = undefined;

  const showSuggestions = !chips || chips.length === 0;

  return (
    <View flex={1} minHeight={minHeight}>
      <Typer
        {...typerProps}
        onTypingComplete={() => setIsTypingComplete(true)}
      />
      {showSuggestions && (
        <View // Hide until typing is done
          opacity={!isTypingComplete ? 0 : 1}
          marginTop="$400"
        >
          <YStack
            gap="$200"
            flexWrap="wrap"
            justifyContent="flex-end"
            alignItems="flex-end"
          >
            {mergedSuggestions.map((suggestion, idx) => (
              <Suggestion
                visibility={idx < visibleSuggestions ? "visible" : "hidden"}
                key={suggestion.title || suggestion.prompt}
                text={suggestion.prompt}
                onPress={() =>
                  sendNoriMessage(suggestion.prompt, {
                    agent: suggestion.agent,
                  })
                }
                disabled={isPending}
                mode="secondary"
              />
            ))}
          </YStack>
        </View>
      )}
    </View>
  );
}
